import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatErr } from "@/lib/api";
import { useLang } from "@/i18n";
import { UploadCloud, FileAudio, FileVideo, ImageIcon, FileText, FileType, Trash2, ArrowRight } from "lucide-react";

const iconFor = (cat) => {
  switch (cat) {
    case "audio": return FileAudio;
    case "video": return FileVideo;
    case "image": return ImageIcon;
    case "pdf": return FileType;
    case "text": return FileText;
    default: return FileText;
  }
};

const fmtBytes = (b) => {
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let n = b || 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
};

export default function UploadCenter() {
  const { t } = useLang();
  const nav = useNavigate();
  const inputRef = useRef();
  const [uploaded, setUploaded] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (filelist) => {
    setErr("");
    const arr = Array.from(filelist);
    if (!arr.length) return;
    setBusy(true);
    try {
      let i = 0;
      const results = [];
      for (const file of arr) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round(((i + e.loaded / e.total) / arr.length) * 100));
          },
        });
        results.push(data);
        i += 1;
      }
      setUploaded((u) => [...u, ...results]);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
  };

  const removeOne = (id) => setUploaded((u) => u.filter((x) => x.id !== id));

  const totalSize = uploaded.reduce((s, x) => s + (x.size || 0), 0);

  const continueToCapsule = () => {
    if (!uploaded.length) return;
    sessionStorage.setItem("s2s_capsule_files", JSON.stringify(uploaded.map((x) => x.id)));
    nav("/capsule");
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="label-overline">Stage 1 / Mission</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="upload-title">{t("upload.title")}</h1>
        <p className="text-white/55 mt-2">{t("upload.subtitle")}</p>
      </div>

      <div
        data-testid="upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative holo-panel p-12 cursor-pointer border border-dashed transition-all ${
          dragOver ? "border-[#00D4FF] pulse-glow" : "border-[#00D4FF]/30"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="upload-input"
        />
        <div className="flex flex-col items-center text-center">
          <UploadCloud size={42} className="text-[#00D4FF] float" />
          <div className="font-display font-black text-2xl mt-6 tracking-tighter">{t("upload.dropzone")}</div>
          <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-3">{t("upload.hint")}</div>

          {busy && (
            <div className="mt-6 w-full max-w-xl">
              <div className="h-1 bg-white/5 relative overflow-hidden">
                <div className="absolute left-0 top-0 h-full bg-[#00D4FF]" style={{ width: `${progress}%` }} />
              </div>
              <div className="font-mono-data text-[11px] text-[#00D4FF] mt-2 tracking-[0.2em] uppercase">
                {t("upload.uploading")} {progress}%
              </div>
            </div>
          )}
        </div>
      </div>

      {err && <div className="mt-4 text-[#FF3366] font-mono-data text-xs" data-testid="upload-error">{err}</div>}

      {uploaded.length > 0 && (
        <div className="mt-8" data-testid="uploaded-list">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-overline">{uploaded.length} {t("upload.files_selected")}</div>
              <div className="font-display font-black text-xl mt-1 tracking-tighter">{t("upload.package")}: {fmtBytes(totalSize)}</div>
            </div>
            <button
              onClick={continueToCapsule}
              className="btn-primary-s2s"
              data-testid="upload-continue"
            >
              {t("upload.next")} <ArrowRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {uploaded.map((f) => {
              const Icon = iconFor(f.category);
              return (
                <div key={f.id} className="holo-panel p-4" data-testid={`uploaded-file-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 grid place-items-center border border-[#00D4FF]/40">
                      <Icon size={16} className="text-[#00D4FF]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{f.original_filename}</div>
                      <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/50 mt-0.5">{f.category} · {fmtBytes(f.size)}</div>
                    </div>
                    <button
                      onClick={() => removeOne(f.id)}
                      data-testid={`remove-file-${f.id}`}
                      className="text-white/40 hover:text-[#FF3366]"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {f.ai_description && (
                    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`ai-desc-${f.id}`}>
                      <div className="label-overline mb-1">AI · Curator</div>
                      <div className="text-[12px] text-white/75 leading-relaxed">{f.ai_description}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </AppShell>
  );
}
